'use client';

import { useState } from 'react';
import { ChevronRight, ChevronLeft, PartyPopper, AlertTriangle } from 'lucide-react';

interface RegistrationCompleteModalProps {
  onFinish: () => void;
}

export function RegistrationCompleteModal({ onFinish }: RegistrationCompleteModalProps) {
  const [currentScreen, setCurrentScreen] = useState(0);
  const totalScreens = 4;
  const isLastScreen = currentScreen === totalScreens - 1;

  const handleNext = () => {
    if (isLastScreen) {
      onFinish();
    } else {
      setCurrentScreen((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentScreen((prev) => Math.max(0, prev - 1));
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Registration tour complete"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8">
        {/* Icon */}
        <div className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full ${
          currentScreen === 1 ? 'bg-amber-100' : 'bg-accent-blue/[0.08]'
        }`}>
          {currentScreen === 1
            ? <AlertTriangle className="h-8 w-8 text-amber-600" />
            : <PartyPopper className="h-8 w-8 text-accent-blue" />
          }
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-900">
          {currentScreen === 0
            ? 'Hosting vs. Porting'
            : currentScreen === 1
              ? 'Important: Accuracy Required'
              : currentScreen === 2
                ? 'Pricing & Compliance'
                : 'You\'re Ready!'}
        </h2>

        {/* Content */}
        {currentScreen === 0 && (
          <div className="text-left space-y-4 mb-8">
            <p className="text-base leading-relaxed text-gray-500">
              When registering an existing phone number, you have two options:
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-semibold text-gray-900 mb-1">Host a Number</p>
                <p className="text-sm text-gray-600">
                  Keep your existing voice service (calls) and add SMS capability through Voley.
                  Your current phone provider continues handling calls while Voley handles text
                  messaging. Best for landlines you want to text-enable.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-semibold text-gray-900 mb-1">Port a Number</p>
                <p className="text-sm text-gray-600">
                  Fully transfer your number from its current carrier to Voley. This moves both
                  voice and SMS to our platform. Best when you want to consolidate services or
                  switch carriers entirely. Takes 5&ndash;10 business days.
                </p>
              </div>
            </div>
          </div>
        )}

        {currentScreen === 1 && (
          <div className="text-left space-y-4 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-2">10DLC Business Information</p>
              <p className="text-sm text-gray-600">
                Your 10DLC application requires your <strong>exact legal business name, EIN, and address</strong> as
                registered with the IRS. Carriers verify this information against public records &mdash; any
                discrepancies will result in your application being <strong>rejected</strong>.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-2">Number Hosting &amp; Porting</p>
              <p className="text-sm text-gray-600">
                If you are hosting or porting an existing phone number, the business name and address you
                provide must match the account information on file with your <strong>current carrier</strong>. The
                carrier will verify your identity as the authorized account holder before releasing the number.
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">
                If any information is inaccurate, your application will be rejected and you will need to
                submit a new one. Double-check all details before submitting.
              </p>
            </div>
          </div>
        )}

        {currentScreen === 2 && (
          <div className="text-left space-y-4 mb-8">
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">New phone number</span>
                <span className="text-sm font-semibold text-gray-900">$5 one-time</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Host or port existing number</span>
                <span className="text-sm font-semibold text-gray-900">$20 one-time</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">10DLC business verification</span>
                <span className="text-sm font-semibold text-gray-900">$10 one-time</span>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 mt-4">
              <p className="font-semibold text-gray-900 mb-1">Compliance Pages</p>
              <p className="text-sm text-gray-600">
                As part of 10DLC compliance, Voley automatically generates a Privacy Policy and
                Terms of Service for your agency. These are hosted at:
              </p>
              <div className="mt-2 space-y-1">
                <p className="text-xs font-mono text-accent-blue">
                  letsvoley.com/compliance/your-agency/privacy
                </p>
                <p className="text-xs font-mono text-accent-blue">
                  letsvoley.com/compliance/your-agency/terms
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                These pages are publicly accessible and linked in your SMS opt-in forms. They are
                generated automatically when your registration is submitted.
              </p>
            </div>
          </div>
        )}

        {currentScreen === 3 && (
          <div className="text-left space-y-4 mb-8">
            <p className="text-base leading-relaxed text-gray-500">
              You now understand how phone number registration and 10DLC verification work.
              When you&rsquo;re ready, click &ldquo;Submit New Registration&rdquo; on this page to get started.
            </p>
            <p className="text-sm leading-relaxed text-gray-500">
              The Complete Registration flow will walk you through everything step by step.
              If you have questions at any time, our support team is available
              at <span className="font-medium text-gray-700">support@voley.us</span>.
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          {currentScreen > 0 ? (
            <button
              onClick={handleBack}
              aria-label="Previous screen"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </button>
          ) : (
            <div />
          )}

          <button
            onClick={handleNext}
            aria-label={isLastScreen ? 'Finish tour' : 'Next screen'}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full text-base font-semibold text-white bg-accent-blue hover:brightness-110 transition-all shadow-md"
          >
            {isLastScreen ? (
              'Finish Tour'
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-1.5 mt-5">
          {Array.from({ length: totalScreens }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === currentScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
